
import { Prop, Mixins, Component, Watch } from "vue-property-decorator";
import api from "@/services/api.service";
import SystemViewTreeNode from '@/types/sv-data/system-view/SystemViewTreeNode';
import { SetupSummaryResponse, SystemViewAlarmOverview } from '@/types/sv-data/system-view/SetupSummary';
import { Getter, namespace, Action } from 'vuex-class';
import { UserPermissions, FeaturesList } from '@/store/types';
import { get } from 'lodash';
import SystemViewMap from "./SystemViewMap.vue";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { MapLayerItem } from "@/types/sv-data/system-view/MapLayerItem";
import SystemViewAlarmAutomations from './SystemViewAlarmAutomations.vue';
import { truncateString } from "@/filters";
import HideMapMixin from "@/mixins/HideMapMixin";
import customFieldsApi from "@/services/api.customField.service";
import CustomDefinedFieldsDisplay from "@/components/custom-fields/CustomDefinedFieldsDisplay.vue";
import { CustomFieldDto, CustomFieldTableTypes, CustomFieldValues } from "@/store/custom-fields/types";
import { FilteredEvent } from '@/store/eventqueue/types';
import { EventShareUser } from '@/store/site-monitor/types';
import RegionalHandlerMixin from '@/mixins/RegionalHandlerMixin';

const CustomFields = namespace("customFields");
const SiteMonitor = namespace("siteMonitor");

@Component({
	components:{
		"system-view-map": SystemViewMap,
		"system-view-alarm-automations": SystemViewAlarmAutomations,
		"custom-fields-display": CustomDefinedFieldsDisplay,
		VuePerfectScrollbar
	},
	filters: {
		truncateString
	}
})
export default class SetupOverviewAlarm extends Mixins(HideMapMixin, RegionalHandlerMixin) {

	@Prop({ required: true, default: null }) private selectedNode: SystemViewTreeNode;

	@Getter getPermissions: UserPermissions;
	@Getter getFeaturesList: FeaturesList;
	@Getter getUserId: number;

	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;

	@Action openEventReview: ({ eventId, hideNav }: { eventId: number, hideNav?: boolean }) => void;

	@CustomFields.Action private retrieveCustomFields: ({ tableType: number, live: boolean }) => Promise<void>;
	@CustomFields.State private alarmCustomFields: CustomFieldDto[];

	@SiteMonitor.Getter("getIsController") isController!: boolean;
	@SiteMonitor.Getter getEventShare: EventShareUser[];
	@SiteMonitor.Action eventPassControl: (payload: { eventID: number; userID: number }) => Promise<any>;
	@SiteMonitor.Action parkEvent: (payload: { eventId: number; hours: number; minutes: number; seconds: number; parkNote: string; parkedByMap: boolean }) => Promise<any>;
	@SiteMonitor.Action leaveEventRequest: (eventId: number) => Promise<any>;

	private setupResponse: SetupSummaryResponse =  null;
	private alarmSummary: SystemViewAlarmOverview = null;
	private locationDetails: MapLayerItem[] = [];
	private customFieldsForObject: CustomFieldValues[] = [];
	private maxCharacters: number = 27;
	private pendingNavigationRow: FilteredEvent;

	private readonly alarmCustomFieldType = CustomFieldTableTypes.Alarm;
	private readonly defaultMaxCharacters = 27;
	private readonly maxCharacterDivider = 70

	private async mounted(): Promise<void> {
		await this.retrieveAlarmDetails();

		window.addEventListener("resize", this.onWindowResized);
	}

	@Watch("selectedNode", { deep: true })
	private async retrieveAlarmDetails(): Promise<void> {
		if(this.selectedNode && this.canUseSystemView)
		{
			this.alarmSummary = null; // Reset previous display data

			this.setupResponse = await api.getSetupSummary(this.selectedNode.objectTypeId, this.selectedNode.objectId)

			if(this.setupResponse && !this.setupResponse?.error ) {
				try {
					this.alarmSummary =  JSON.parse(this.setupResponse.summary) as SystemViewAlarmOverview;
				}
				catch (e) {
					console.error("Failed to Parse Alarm summary: " + e);
					this.setupResponse.error = "Failed to Parse Alarm summary";
				} finally {
					this.updateMapData();
				}
			}

			if (this.isCustomFieldAlarmsEnabled) {
				this.customFieldsForObject = await customFieldsApi.retrieveCustomFieldsForObject(this.selectedNode.objectId, CustomFieldTableTypes.Alarm);
			}
		}
	}

	private get canUseSystemView(): boolean {
		return get(this.getFeaturesList, ["SystemView"]) && (this.getPermissions.canViewSystemView || this.getPermissions.isSystemAdmin);
	}

	private get showAutomations(): boolean {
		return !!get(this.getFeaturesList, ["Alarms", "CameraAutomation"]);
	}

	private get isCustomFieldAlarmsEnabled(): boolean {
		return get(this.getFeaturesList, ["CustomFields", "Alarms"]);
	}

	private get isNonRestoredAlarmsEnabled(): boolean {
		return this.getFeature(["SystemView", "AdvancedStatusFilters", "NonRestoredAlarms"])
	}

	private get getSystemOverviewCols(): string{
		return this.isHideMapsEnabled ? '12' : '6';
	}

	private updateMapData(): void {
		if (!!this.alarmSummary && !!this.alarmSummary.mapLocations) {
			this.locationDetails = this.alarmSummary.mapLocations;
		} else {
			this.locationDetails = [];
		}
	}

	private get alarmGrouping(): string {
		switch(this.alarmSummary.isRaiseIndividual) {
			case false:
				return this.alarmSummary.isRaiseGrouped ? "By Alarm" : "By Area";
			case true:
				return "Not Grouped";
			default:
				return "Not Set"
		}
	}

	private get hasLinkedCameras(): boolean {
		return !!this.alarmSummary.automations && this.alarmSummary.automations.length > 0;
	}

	private get serverTypeEventTitle(): string {
		return this.alarmSummary?.serverTypeEventTitle ?? "Default" ;
	}

	private onWindowResized(): void {
		this.maxCharacters = Math.min(this.defaultMaxCharacters, window.innerWidth / this.maxCharacterDivider);
	}

	private convertToFilteredEvent(alarmSummary: SystemViewAlarmOverview): FilteredEvent {
		return {
		age: 0,
		alarmCount: 0,
		alarmTypeID: null,
		awaitingRestoreCount: 0,
		created: '',
		displayTitle: this.alarmSummary.title || '',
		eventID: this.alarmSummary?.awaitingRestoreEventId?.eventId,
		eventTitle: this.alarmSummary.title || '',
		eventTypeID: 1,
		externalSystemInfo: null,
		groupCreated: '',
		groupID: this.alarmSummary.groupId,
		groupTitle: '',
		groupUnparkAt: null,
		inFilters: null,
		inProcessing: false,
		isCCure: false,
		isTrouble: false,
		lastActivity: null,
		lastUnparkedAt: '',
		location: '',
		needsSilence: false,
		priority: 0,
		raisedByVideowall: false,
		regionID: 0,
		regionTitle: '',
		regionURL: '',
		responseID: null,
		responseTitle: '',
		routingGroupColourCode: '',
		routingGroupID: 0,
		routingGroupTitle: '',
		timezone: null,
		unParkAt: null,
		userID: null,
		userUnparkAt: null,
		username: '',
		viewedByVideowall: false,
		priorityStyle: '',
		slaBreachAt: null,
		slaBreachRaised: null
		};
	}

	private autoPassAlarmEnabled(): boolean {
		return this.getFeature(["Alarms.AutoPassAlarmOnLeave"]);
	}

	private async leaveEvent(): Promise<void> {
		if (this.alarmSummary.awaitingRestoreEventId) {
			if (this.isController) {
				// Handle auto-pass alarm if enabled
				if (this.autoPassAlarmEnabled()) {
					let sharedOperators = this.getEventShare.filter((user: any) =>
						user.userID != null &&
						user.userID != this.getUserId &&
						!user.removed &&
						user.isActive
					);
					if (sharedOperators != null && sharedOperators.length == 1) {
						await this.eventPassControl({
							eventID: this.alarmSummary?.awaitingRestoreEventId?.eventId,
							userID: sharedOperators[0].userID
						});
					} else {
						// Park the event if we can't pass control
						await this.parkEvent({
							eventId: this.alarmSummary?.awaitingRestoreEventId?.eventId,
							hours: 0,
							minutes: 0,
							seconds: 0,
							parkNote: "",
							parkedByMap: false
						});
					}
				} else {
					// Park the event if auto-pass is not enabled
					await this.parkEvent({
						eventId: this.alarmSummary?.awaitingRestoreEventId?.eventId,
						hours: 0,
						minutes: 0,
						seconds: 0,
						parkNote: "",
						parkedByMap: false
					});
				}
			} else {
				// If not controller, just leave the event
				await this.leaveEventRequest(this.alarmSummary?.awaitingRestoreEventId?.eventId);
			}
		}
	}

	private async handleEventClick(): Promise<void> {
		if (!this.alarmSummary?.awaitingRestoreEventId?.eventClosed) {
			this.pendingNavigationRow = this.convertToFilteredEvent(this.alarmSummary);
			await this.leaveEvent();
			await this.handleEventQueueItem(
				this.pendingNavigationRow,
				undefined,
				true,
				'Picked up from System View',
				false,
				true
			);
		} else {
			this.openEventReview({eventId: this.alarmSummary?.awaitingRestoreEventId?.eventId, hideNav: true});
		}
	}
}
